import { dialogService } from "@/utils/dialogService";

const DIRECT_MESSAGE_KEYS = ["detail", "message", "error"] as const;

const collectMessages = (input: unknown, prefix?: string): string[] => {
  if (input === null || input === undefined) {
    return [];
  }

  if (typeof input === "string" || typeof input === "number" || typeof input === "boolean") {
    const message = String(input).trim();
    if (!message) {
      return [];
    }
    return [prefix ? `${prefix}: ${message}` : message];
  }

  if (input instanceof Error) {
    const message = input.message || input.name;
    return message ? [prefix ? `${prefix}: ${message}` : message] : [];
  }

  if (Array.isArray(input)) {
    return input.flatMap((value) => collectMessages(value, prefix));
  }

  if (typeof input === "object") {
    const record = input as Record<string, unknown>;
    const directMessages = DIRECT_MESSAGE_KEYS.flatMap((key) => collectMessages(record[key]));
    const nonFieldMessages = collectMessages(record.non_field_errors);
    const nestedEntries = Object.entries(record)
      .filter(([key]) => key !== "non_field_errors" && !DIRECT_MESSAGE_KEYS.includes(key as (typeof DIRECT_MESSAGE_KEYS)[number]))
      .flatMap(([key, value]) => collectMessages(value, key));

    return [...directMessages, ...nonFieldMessages, ...nestedEntries];
  }

  const normalized = dialogService.normalizeMessage(input).trim();
  return normalized ? [prefix ? `${prefix}: ${normalized}` : normalized] : [];
};

export const getErrorToastMessage = (error: unknown, fallbackMessage: string) => {
  const responsePayload =
    error && typeof error === "object" && "response" in error
      ? (error as { response?: { data?: unknown } }).response?.data
      : undefined;

  const messages = collectMessages(responsePayload ?? error)
    .map((message) => message.trim())
    .filter((message) => {
      return Boolean(message) && message !== "{}" && message !== "[object Object]";
    });

  const uniqueMessages = [...new Set(messages)];
  if (uniqueMessages.length > 0) {
    return uniqueMessages.join(" | ");
  }

  const normalized = dialogService.normalizeMessage(error).trim();
  if (normalized && normalized !== "{}" && normalized !== "[object Object]") {
    return normalized;
  }

  return fallbackMessage;
};